<template>
  <div
    ref="selectionSurface"
    class="audit-log-selection-surface">
    <div
      v-if="selectionBox"
      class="audit-selection-box"
      :style="selectionBoxStyle">
    </div>

    <div v-if="selectedEntryIDs.length > 0" class="audit-selection-summary">
      <strong>{{ selectedEntryIDs.length }}</strong>
      {{ selectedEntryIDs.length === 1 ? 'entry selected' : 'entries selected' }}
      <button type="button" class="btn btn-link" @click="selectedEntryIDs = []">
        Clear
      </button>
    </div>

    <div v-if="displayRecords.length === 0 && pinnedGroups.length === 0" class="audit-empty-state">
      No audit log entries match the current filters.
    </div>

    <div
      v-if="groupsEnabled && pinnedGroups.length > 0"
      ref="pinnedAnchor"
      class="audit-pinned-anchor"
      :style="pinnedAnchorStyle">
      <div
        ref="pinnedGroups"
        class="audit-pinned-groups"
        :class="{ 'audit-pinned-groups-fixed': pinnedGroupsFixed }"
        :style="pinnedGroupsStyle">
        <AuditLogGroupCard
          v-for="group in pinnedGroups"
          :key="'pinned-group-' + group.id"
          :group="group"
          :editable="editableGroups"
          :editable-evidence="editableEvidence"
          :allow-delete-entries="allowDeleteEntries"
          :allow-delete-manual-entries="allowDeleteManualEntries"
          :search-query="searchQuery"
          :groups-enabled="groupsEnabled"
          :pinned="true"
          :drop-target="isGroupDropTarget(group.id)"
          @context-menu="openGroupContextMenu"
          @toggle-pin="toggleGroupPin"
          @request-delete="openDeleteGroupModal"
          @request-delete-entry="confirmDeleteEntry"
          @evidence-changed="$emit('evidence-changed')"
          @drag-over="dragOverGroup"
          @drag-leave="leaveDropTarget"
          @drop="dropOnGroup">
        </AuditLogGroupCard>
      </div>
    </div>

    <div
      v-if="groupContextMenu.visible"
      class="audit-group-context-menu"
      :style="{ left: groupContextMenu.x + 'px', top: groupContextMenu.y + 'px' }"
      @click.stop>
      <button
        v-if="groupContextMenu.targetType === 'group'"
        type="button"
        @click="toggleContextMenuGroupPin">
        <i :class="isPinnedGroup(groupContextMenu.group.id) ? 'fa fa-thumb-tack' : 'fa fa-thumb-tack'"></i>
        {{ isPinnedGroup(groupContextMenu.group.id) ? 'Unpin group' : 'Pin group' }}
      </button>
      <button v-if="groupsEnabled" type="button" @click="openAddFieldModal">
        <i class="fa fa-plus-square-o"></i>
        Add Fields
      </button>
      <button v-if="groupsEnabled" type="button" @click="openAddTagModal">
        <i class="fa fa-tags"></i>
        Add Tags
      </button>
      <button v-if="groupsEnabled" type="button" @click="openEvidenceUpload">
        <i class="fa fa-upload"></i>
        Upload Evidence
      </button>
    </div>

    <div class="list-group audit-log-list">
      <template v-for="record in displayRecords">
        <article
          v-if="record.kind === 'group'"
          :key="'group-' + record.group.id"
          class="list-group-item audit-log-entry audit-log-group"
          :class="{ 'audit-drop-target': isGroupDropTarget(record.group.id) }"
          @contextmenu.prevent="openGroupContextMenu($event, record.group)"
          @dragenter.prevent="dragOverGroup($event, record.group)"
          @dragover.prevent="dragOverGroup($event, record.group)"
          @dragleave="leaveDropTarget"
          @drop.stop="dropOnGroup(record.group)">
          <div class="audit-log-date">
            <small v-b-tooltip.hover.html="groupDateTooltip(record.group)">{{ groupDate(record.group) }}</small>
          </div>
          <div class="d-flex audit-log-main-row audit-group-summary">
            <button
              type="button"
              class="audit-expand-button"
              :title="isExpanded(record.group.id) ? 'Collapse group' : 'Expand group'"
              @click.stop="toggleExpanded(record.group.id)">
              <i :class="isExpanded(record.group.id) ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
            </button>
            <span
              v-if="editableGroups && groupsEnabled"
              class="audit-entry-checkbox audit-card-checkbox audit-checkbox-placeholder"
              aria-hidden="true">
            </span>
            <div class="audit-log-icon audit-group-icon">
              <i class="fa fa-object-group"></i>
            </div>
            <div class="pl-3">
              <img
                :src="'https://pokemonrevolution.net/forum/avatar.php?user=' + record.group.username"
                alt=""
                height="50"
                width="50"
                loading="lazy"
                decoding="async"
                class="rounded-circle">
            </div>
            <div class="audit-log-content pl-3">
              <div class="audit-log-meta">
                <span
                  class="audit-staff-name"
                  :style="{ color: groupRoleColour(record.group) }">
                  {{ record.group.username }}
                </span>
                <span
                  v-for="server in groupServers(record.group)"
                  :key="'group-' + record.group.id + '-' + server"
                  :class="'badge-' + getColour(server)"
                  class="badge text-capitalize">
                  {{ server }}
                </span>
                <span v-if="record.group.entries.length < record.group.total_member_count" class="badge badge-secondary">
                  {{ record.group.entries.length }} / {{ record.group.total_member_count }} visible
                </span>
                <div
                  class="audit-reason-row audit-reason-inline"
                  :class="{ 'audit-reason-missing': !record.group.reason && !isEditingGroupReason(record.group.id) }">
                  <template v-if="isEditingGroupReason(record.group.id)">
                    <textarea
                      v-audit-resize="'reason'"
                      v-model="draftReasons[record.group.id]"
                      class="form-control audit-reason-editor"
                      rows="2"
                      maxlength="2000"
                      @click.stop
                      @keydown.enter.prevent="saveGroupReason(record.group)"
                      @keydown.esc.prevent.stop="cancelGroupReasonEdit(record.group)"
                      @blur="saveGroupReason(record.group)">
                    </textarea>
                  </template>
                  <template v-else>
                    <mark
                      v-if="record.group.reason"
                      class="auditReason"
                      @click.stop>
                      <template v-for="(segment, index) in reasonSegments(record.group.reason)">
                        <a
                          v-if="segment.url"
                          :key="'group-url-' + record.group.id + '-' + index"
                          href="#"
                          @click.prevent="confirmExternalLink(segment.text)">
                          <AuditLogHighlight :text="segment.text" :query="searchQuery"/>
                        </a>
                        <AuditLogHighlight
                          v-else
                          :key="'group-text-' + record.group.id + '-' + index"
                          :text="segment.text"
                          :query="searchQuery"/>
                      </template>
                    </mark>
                    <button
                      v-if="editableGroups"
                      type="button"
                      class="btn btn-link audit-reason-edit"
                      :class="record.group.reason ? 'text-muted' : 'text-danger'"
                      :title="record.group.reason ? 'Edit reason' : 'Add reason'"
                      @click.stop="startGroupReasonEdit(record.group)">
                      <i class="fa fa-pencil"></i>
                      <span v-if="!record.group.reason && groupIsImportant(record.group)">Add required reason</span>
                    </button>
                  </template>
                </div>
              </div>

              <div class="audit-group-title-row">
                <input
                  v-if="editableGroups && isEditingGroupTitle(record.group.id)"
                  v-model="draftTitles[record.group.id]"
                  class="form-control audit-group-title-input"
                  :data-group-id="record.group.id"
                  maxlength="120"
                  @click.stop
                  @keydown.enter.prevent="$event.target.blur()"
                  @keydown.esc.prevent.stop="cancelGroupTitleEdit(record.group)"
                  @blur="saveGroupTitle(record.group)">
                <button
                  v-else-if="editableGroups"
                  type="button"
                  class="audit-group-title audit-group-title-button"
                  title="Edit group title"
                  @click.stop="startGroupTitleEdit(record.group)">
                  <AuditLogHighlight :text="record.group.title" :query="searchQuery"/>
                </button>
                <strong v-else class="audit-group-title">
                  <AuditLogHighlight :text="record.group.title" :query="searchQuery"/>
                </strong>
                <span v-if="editableGroups" class="audit-group-actions">
                  <button
                    v-if="!isEditingGroupTitle(record.group.id)"
                    type="button"
                    class="btn btn-link text-muted audit-title-edit"
                    title="Edit group title"
                    @click.stop="startGroupTitleEdit(record.group)">
                    <i class="fa fa-pencil"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-link text-danger audit-delete-group"
                    title="Delete group"
                    @click.stop="openDeleteGroupModal(record.group)">
                    <i class="fa fa-trash"></i>
                  </button>
                </span>
              </div>
              <AuditLogGroupMetadata
                :fields="record.group.fields || []"
                :tags="record.group.tags || []"
                :search-query="searchQuery"
                :editable="editableGroups">
              </AuditLogGroupMetadata>
              <AuditLogEvidence
                :items="record.group.evidence || []"
                :target-id="record.group.id"
                :editable="editableEvidence"
                target-type="group"
                @evidence-changed="$emit('evidence-changed')">
              </AuditLogEvidence>
              <small class="text-muted audit-action-label">
                Group ({{ record.group.total_member_count || record.group.entries.length }} entries)
              </small>

            </div>
          </div>

          <div
            v-if="isExpanded(record.group.id)"
            class="audit-group-members"
            :class="{ 'audit-group-members-selectable': editableGroups && groupsEnabled }">
            <div
              v-for="(entry, index) in sortedGroupEntries(record.group)"
              :key="'group-entry-' + record.group.id + '-' + entry.id"
              class="audit-group-member"
              :class="{
                'audit-group-member-editable': editableGroups,
                'audit-group-member-deletable': canDeleteEntry(entry)
              }">
              <span class="audit-group-number">{{ index + 1 }}.</span>
              <i
                v-b-tooltip.hover="entry.manual ? 'Self-created activity log entry' : ''"
                :class="[getIcon(entry.type), { 'audit-manual-entry-icon': entry.manual }]"
                class="audit-group-entry-icon">
              </i>
              <div class="audit-group-member-content">
                <span class="audit-group-message">
                  <AuditLogHighlight :text="entry.message" :query="searchQuery"/>
                </span>
                <span class="audit-group-member-servers">
                  <span
                    v-for="server in sortedServers(entry.servers)"
                    :key="'group-entry-' + record.group.id + '-' + entry.id + '-' + server"
                    :class="'badge-' + getColour(server)"
                    class="badge text-capitalize">
                    {{ server }}
                  </span>
                </span>
              </div>
              <small class="audit-group-member-date">{{ formatDateDisplay(entry.date) }}</small>
              <button
                v-if="editableGroups"
                type="button"
                class="audit-remove-member"
                title="Remove from group"
                @click.stop="removeFromGroup(record.group, entry)">
                <i class="fa fa-times"></i>
              </button>
              <button
                v-if="canDeleteEntry(entry)"
                type="button"
                class="audit-delete-entry"
                title="Delete entry"
                @click.stop="confirmDeleteEntry(entry)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </article>

        <article
          v-else
          :key="'entry-' + record.entry.id"
          :data-entry-id="record.entry.id"
          class="list-group-item audit-log-entry"
          :class="{
            'audit-entry-selected': isSelected(record.entry.id),
            'audit-drop-target': isEntryDropTarget(record.entry.id)
          }"
          @contextmenu="openEntryContextMenu($event, record.entry)"
          @dragenter.prevent="dragOverEntry($event, record.entry)"
          @dragover.prevent="dragOverEntry($event, record.entry)"
          @dragleave="leaveDropTarget"
          @drop.stop="dropOnEntry(record.entry)">
          <div class="audit-log-date">
            <small v-b-tooltip.hover.html="getDateTooltip(record.entry.date)">
              {{ formatDateDisplay(record.entry.date) }}
            </small>
          </div>

          <div class="d-flex audit-log-main-row">
            <label
              v-if="editableGroups && groupsEnabled"
              class="audit-entry-checkbox audit-card-checkbox"
              title="Select entry"
              @click.stop>
              <input
                type="checkbox"
                :checked="isSelected(record.entry.id)"
                @mousedown.stop
                @change.stop="setEntrySelected(record.entry.id, $event.target.checked)">
            </label>
            <div
              v-b-tooltip.hover="record.entry.manual
                ? (isSelected(record.entry.id) ? 'Self-created activity log entry. Drag selected entries.' : 'Self-created activity log entry')
                : (isSelected(record.entry.id) ? 'Drag selected entries' : '')"
              class="audit-log-icon"
              :class="{
                'audit-manual-entry-icon': record.entry.manual,
                'audit-entry-drag-handle': editableGroups && groupsEnabled && isSelected(record.entry.id)
              }"
              :draggable="editableGroups && groupsEnabled && isSelected(record.entry.id)"
              @mousedown.stop
              @dragstart.stop="startDrag($event, record.entry.id)"
              @dragend="finishDrag">
              <i :class="getIcon(record.entry.type)"></i>
            </div>
            <div class="pl-3">
              <img
                :src="'https://pokemonrevolution.net/forum/avatar.php?user=' + record.entry.username"
                alt=""
                height="50"
                width="50"
                loading="lazy"
                decoding="async"
                class="rounded-circle">
            </div>

            <div class="audit-log-content pl-3">
              <div class="audit-log-meta">
                <span
                  class="audit-staff-name"
                  :style="{ color: roleColour(record.entry) }"
                  :title="roleName(record.entry)">
                  {{ record.entry.username }}
                </span>
                <span
                  v-for="server in sortedServers(record.entry.servers)"
                  :key="record.entry.id + '-' + server"
                  :class="'badge-' + getColour(server)"
                  class="badge text-capitalize">
                  {{ server }}
                </span>
                <div
                  class="audit-reason-row audit-reason-inline"
                  :class="{ 'audit-reason-missing': !record.entry.reason && !isEditingEntryReason(record.entry.id) }">
                  <template v-if="isEditingEntryReason(record.entry.id)">
                    <textarea
                      v-audit-resize="'reason'"
                      v-model="draftEntryReasons[record.entry.id]"
                      class="form-control audit-reason-editor"
                      rows="2"
                      maxlength="2000"
                      @click.stop
                      @keydown.enter.prevent="saveEntryReason(record.entry)"
                      @keydown.esc.prevent.stop="cancelEntryReasonEdit(record.entry)"
                      @blur="saveEntryReason(record.entry)">
                    </textarea>
                  </template>
                  <template v-else>
                    <mark
                      v-if="record.entry.reason"
                      class="auditReason"
                      @click.stop>
                      <template v-for="(segment, index) in reasonSegments(record.entry.reason)">
                        <a
                          v-if="segment.url"
                          :key="'entry-url-' + record.entry.id + '-' + index"
                          href="#"
                          @click.prevent="confirmExternalLink(segment.text)">
                          <AuditLogHighlight :text="segment.text" :query="searchQuery"/>
                        </a>
                        <AuditLogHighlight
                          v-else
                          :key="'entry-text-' + record.entry.id + '-' + index"
                          :text="segment.text"
                          :query="searchQuery"/>
                      </template>
                    </mark>
                    <button
                      v-if="editableReasons"
                      type="button"
                      class="btn btn-link audit-reason-edit"
                      :class="record.entry.reason ? 'text-muted' : 'text-danger'"
                      :title="record.entry.reason ? 'Edit reason' : 'Add reason'"
                      @click.stop="startEntryReasonEdit(record.entry)">
                      <i class="fa fa-pencil"></i>
                      <span v-if="!record.entry.reason && record.entry.warning">Add required reason</span>
                    </button>
                  </template>
                </div>
              </div>

              <span class="audit-log-message">
                <AuditLogHighlight :text="record.entry.message" :query="searchQuery"/>
              </span>
              <AuditLogGroupMetadata
                v-if="groupsEnabled"
                :fields="record.entry.fields || []"
                :tags="record.entry.tags || []"
                :search-query="searchQuery"
                :editable="editableGroups"
                target-type="entry">
              </AuditLogGroupMetadata>
              <AuditLogEvidence
                :items="record.entry.evidence || []"
                :target-id="record.entry.id"
                :editable="editableEvidence"
                target-type="entry"
                @evidence-changed="$emit('evidence-changed')">
              </AuditLogEvidence>
              <small class="text-muted audit-action-label">{{ actionLabel(record.entry.type) }}</small>
            </div>
            <button
              v-if="canDeleteEntry(record.entry)"
              type="button"
              class="audit-delete-entry audit-delete-entry-card"
              title="Delete entry"
              @click.stop="confirmDeleteEntry(record.entry)">
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </article>
      </template>
    </div>

    <b-modal
      v-model="fieldModal.visible"
      title="Add Fields"
      size="lg"
      centered
      header-bg-variant="dark"
      header-text-variant="light"
      body-bg-variant="dark"
      body-text-variant="light"
      footer-bg-variant="dark"
      footer-text-variant="light"
      ok-title="Apply Fields"
      @ok.prevent="applyFields">
      <div class="audit-field-picker-header">
        <span>Add</span>
        <span>Field</span>
        <span>Set as default</span>
        <span aria-hidden="true"></span>
      </div>
      <div
        v-for="(field, index) in fieldModal.fields"
        :key="'field-choice-' + field.type + '-' + field.name"
        class="audit-field-picker-row">
        <input v-model="field.selected" type="checkbox" :aria-label="'Add ' + field.label">
        <span>{{ field.label }}</span>
        <input
          v-model="field.setAsDefault"
          type="checkbox"
          :aria-label="'Set ' + field.label + ' as default'"
          @change="selectDefaultField(field)">
        <span aria-hidden="true"></span>
      </div>
      <div
        v-for="(field, index) in fieldModal.customFields"
        :key="'custom-field-choice-' + index"
        class="audit-field-picker-row audit-custom-field-row">
        <input v-model="field.selected" type="checkbox" aria-label="Add custom field">
        <input
          v-model="field.name"
          type="text"
          class="form-control"
          maxlength="64"
          placeholder="Custom field name"
          @input="sanitizeCustomFieldName(field)">
        <input
          v-model="field.setAsDefault"
          type="checkbox"
          aria-label="Set custom field as default"
          @change="selectDefaultField(field)">
        <button type="button" class="btn btn-link text-danger" title="Remove custom field" @click="removeCustomField(index)">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <button type="button" class="btn btn-outline-info btn-sm mt-3" @click="addCustomField">
        <i class="fa fa-plus"></i>
        Add Custom Field
      </button>
    </b-modal>

    <b-modal
      v-model="tagModal.visible"
      title="Add Tags"
      size="lg"
      centered
      header-bg-variant="dark"
      header-text-variant="light"
      body-bg-variant="dark"
      body-text-variant="light"
      footer-bg-variant="dark"
      footer-text-variant="light"
      ok-title="Add Tags"
      :ok-disabled="!hasTagsToAdd"
      @ok.prevent="applyTags">
      <div class="audit-tag-picker-header">
        <span>Add</span>
        <span>Tag</span>
      </div>
      <div
        v-for="tag in tagModal.tags"
        :key="'tag-choice-' + tag.type"
        class="audit-tag-picker-row">
        <input
          v-model="tag.selected"
          type="checkbox"
          :disabled="tag.existing"
          :aria-label="'Add ' + tag.label">
        <span>{{ tag.label }}</span>
        <small v-if="tag.existing" class="text-muted">Already added</small>
      </div>
      <div
        v-for="(tag, index) in tagModal.customTags"
        :key="'custom-tag-choice-' + index"
        class="audit-tag-picker-row audit-custom-tag-row">
        <input
          v-model="tag.selected"
          type="checkbox"
          :disabled="tag.existing"
          aria-label="Add custom tag">
        <input
          v-model="tag.label"
          type="text"
          class="form-control"
          maxlength="64"
          placeholder="Custom tag name"
          :disabled="tag.existing"
          @input="sanitizeCustomTagName(tag)">
        <small v-if="tag.existing" class="text-muted">Already added</small>
        <button
          v-else
          type="button"
          class="btn btn-link text-danger"
          title="Remove custom tag"
          @click="removeCustomTag(index)">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <button type="button" class="btn btn-outline-info btn-sm mt-3" @click="addCustomTag">
        <i class="fa fa-plus"></i>
        Add Custom Tag
      </button>
    </b-modal>

    <b-modal
      v-model="deleteModal.visible"
      title="Delete group?"
      centered
      header-bg-variant="dark"
      header-text-variant="light"
      body-bg-variant="dark"
      body-text-variant="light"
      footer-bg-variant="dark"
      footer-text-variant="light"
      ok-title="Delete Group"
      ok-variant="danger"
      @ok.prevent="deleteGroup">
      <p>The group will be deleted. Its audit log entries will remain.</p>
      <b-form-checkbox v-model="deleteModal.keepReasons">
        Keep the reasons on the individual entries
      </b-form-checkbox>
    </b-modal>

    <b-modal
      v-model="evidenceUpload.visible"
      title="Upload Evidence"
      size="lg"
      centered
      no-close-on-backdrop
      :no-close-on-esc="evidenceUpload.uploading"
      :hide-header-close="evidenceUpload.uploading"
      header-bg-variant="dark"
      header-text-variant="light"
      body-bg-variant="dark"
      body-text-variant="light"
      footer-bg-variant="dark"
      footer-text-variant="light"
      @hidden="resetEvidenceUpload">
      <p class="text-muted">
        Images: PNG, JPG, JPEG up to 5 MB. Videos: MP4, MKV, AVI, MOV, MPG, MPEG up to 100 MB.
        Select files repeatedly or press Ctrl+V to add clipboard files.
      </p>
      <input
        ref="evidenceInput"
        type="file"
        class="form-control-file"
        multiple
        accept=".png,.jpg,.jpeg,.mp4,.mkv,.avi,.mov,.mpg,.mpeg"
        :disabled="evidenceUpload.uploading"
        @change="selectEvidenceFiles">
      <button
        type="button"
        class="btn btn-outline-info btn-sm mt-3"
        :disabled="evidenceUpload.uploading || evidenceUpload.readingClipboard"
        @click="selectEvidenceFromClipboard">
        <i class="fa fa-clipboard"></i>
        {{ evidenceUpload.readingClipboard ? 'Reading Clipboard...' : 'Upload from Clipboard' }}
      </button>
      <div v-if="evidenceUpload.error" class="alert alert-danger mt-3 mb-0">
        {{ evidenceUpload.error }}
      </div>
      <div v-if="evidenceUpload.previews.length" class="audit-upload-preview-grid">
        <div
          v-for="preview in evidenceUpload.previews"
          :key="preview.key"
          class="audit-upload-preview">
          <button
            type="button"
            class="btn btn-danger btn-sm audit-upload-remove"
            title="Remove file"
            :disabled="evidenceUpload.uploading"
            @click="removeEvidenceFile(preview)">
            <i class="fa fa-trash-o"></i>
          </button>
          <img v-if="preview.mediaType === 'image'" :src="preview.url" :alt="preview.file.name">
          <span v-else class="audit-upload-video-icon">
            <i class="fa fa-file-video-o"></i>
          </span>
          <span :title="preview.file.name">{{ preview.file.name }}</span>
          <small>{{ formatEvidenceSize(preview.file.size) }}</small>
        </div>
      </div>
      <b-progress
        v-if="evidenceUpload.uploading"
        class="mt-3"
        :value="evidenceUpload.progress"
        :max="100"
        show-progress
        animated>
      </b-progress>
      <template #modal-footer>
        <b-button variant="secondary" :disabled="evidenceUpload.uploading" @click="evidenceUpload.visible = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="evidenceUpload.uploading || evidenceUpload.files.length === 0"
          @click="uploadEvidence">
          <i class="fa fa-upload"></i>
          {{ evidenceUpload.uploading ? 'Uploading...' : 'Upload Evidence' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import iziToast from 'izitoast'
import Permissions from '../../_permissions'
import Roles from '../../_roles'
import Servers from '../../_servers'
import { buildAuditLogActionGroups } from '../../auditlog_permissions'
import AuditLogGroupCard from './AuditLogGroupCard.vue'
import AuditLogGroupMetadata from './AuditLogGroupMetadata.vue'
import AuditLogEvidence from './AuditLogEvidence.vue'
import AuditLogHighlight from './AuditLogHighlight.vue'

const urlPattern = /(https?:\/\/[^\s<>"']+)/gi
const maxEvidenceImageSize = 5 * 1024 * 1024
const maxEvidenceVideoSize = 100 * 1024 * 1024

export default {
  name: 'AuditLogList',
  provide () {
    return {
      auditLogEditorCoordinator: {
        activate: this.activateAuditLogEditor,
        deactivate: this.deactivateAuditLogEditor
      }
    }
  },
  components: {
    AuditLogGroupCard,
    AuditLogGroupMetadata,
    AuditLogEvidence,
    AuditLogHighlight
  },
  props: {
    entries: {
      type: Array,
      default: () => []
    },
    groups: {
      type: Array,
      default: () => []
    },
    groupsEnabled: {
      type: Boolean,
      default: false
    },
    editableGroups: {
      type: Boolean,
      default: false
    },
    editableReasons: {
      type: Boolean,
      default: false
    },
    editableEvidence: {
      type: Boolean,
      default: false
    },
    allowDeleteEntries: {
      type: Boolean,
      default: false
    },
    allowDeleteManualEntries: {
      type: Boolean,
      default: false
    },
    deleteEntriesFromUserLog: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    },
    defaultFields: {
      type: Array,
      default: () => []
    },
    pinnedGroupIds: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      permissions: Permissions,
      actionLookupGroups: buildAuditLogActionGroups(),
      expandedGroups: {},
      selectedEntryIDs: [],
      draggedEntryID: null,
      dropTarget: null,
      groupContextMenu: {
        visible: false,
        x: 0,
        y: 0,
        targetType: 'group',
        group: null,
        entry: null
      },
      selectionStart: null,
      selectionBox: null,
      selectionBaseIDs: [],
      selectionFrame: null,
      selectionPointer: null,
      activeEditor: null,
      draftTitles: {},
      draftReasons: {},
      draftEntryReasons: {},
      editingGroupTitles: {},
      editingGroupReasons: {},
      editingEntryReasons: {},
      pinnedGroupsFixed: false,
      pinnedGroupsHeight: 0,
      pinnedGroupsBounds: {
        left: 0,
        width: 0,
        top: 64
      },
      pinnedResizeObserver: null,
      fieldOptions: [
        { value: 'appeal_link', label: 'Appeal-Link' },
        { value: 'report_link', label: 'Report-Link' },
        { value: 'usernames', label: 'Username/s' },
        { value: 'punishment', label: 'Punishment' },
        { value: 'comment', label: 'Comment' },
        { value: 'date', label: 'Date' },
        { value: 'custom', label: 'Custom' }
      ],
      tagOptions: [
        { value: 'successfully_appealed', label: 'Successfully Appealed' },
        { value: 'appeal_denied', label: 'Appeal Denied' },
        { value: 'clean', label: 'Clean' },
        { value: 'no_longer_welcome', label: 'No longer welcome' },
        { value: 'benefit_of_the_doubt', label: 'Benefit of the doubt' },
        { value: 'custom', label: 'Custom' }
      ],
      fieldModal: {
        visible: false,
        targetType: 'group',
        target: null,
        fields: [],
        customFields: []
      },
      tagModal: {
        visible: false,
        targetType: 'group',
        target: null,
        tags: [],
        customTags: []
      },
      deleteModal: {
        visible: false,
        group: null,
        keepReasons: true
      },
      evidenceUpload: {
        visible: false,
        targetType: 'entry',
        target: null,
        files: [],
        previews: [],
        uploading: false,
        readingClipboard: false,
        progress: 0,
        error: '',
        previewSequence: 0
      }
    }
  },
  computed: {
    visibleEntryIDs () {
      return new Set(this.entries.map(entry => Number(entry.id)))
    },
    visibleGroups () {
      return this.groups
        .map(group => ({
          ...group,
          entries: this.isPinnedGroup(group.id)
            ? (group.entries || [])
            : (group.entries || []).filter(entry => this.visibleEntryIDs.has(Number(entry.id)))
        }))
        .filter(group => this.isPinnedGroup(group.id) || group.entries.length > 0)
    },
    pinnedGroups () {
      const groupsByID = new Map(this.visibleGroups.map(group => [Number(group.id), group]))
      return this.pinnedGroupIds
        .map(groupID => groupsByID.get(Number(groupID)))
        .filter(Boolean)
    },
    groupedEntryIDs () {
      const grouped = new Set()
      this.visibleGroups.forEach(group => {
        group.entries.forEach(entry => grouped.add(Number(entry.id)))
      })
      return grouped
    },
    displayRecords () {
      if (!this.groupsEnabled) {
        return this.entries
          .map(entry => ({ kind: 'entry', entry, date: new Date(entry.date).getTime() }))
          .sort((left, right) => right.date - left.date)
      }
      const records = this.visibleGroups
        .filter(group => !this.isPinnedGroup(group.id))
        .map(group => ({
          kind: 'group',
          group,
          date: this.newestGroupTimestamp(group)
        }))
      this.entries.forEach(entry => {
        if (!this.groupedEntryIDs.has(Number(entry.id))) {
          records.push({ kind: 'entry', entry, date: new Date(entry.date).getTime() })
        }
      })
      return records.sort((left, right) => right.date - left.date)
    },
    selectionBoxStyle () {
      if (!this.selectionBox) {
        return {}
      }
      return {
        left: this.selectionBox.left + 'px',
        top: this.selectionBox.top + 'px',
        width: this.selectionBox.width + 'px',
        height: this.selectionBox.height + 'px'
      }
    },
    pinnedAnchorStyle () {
      if (!this.pinnedGroupsFixed) {
        return {}
      }
      return {
        height: this.pinnedGroupsHeight + 'px'
      }
    },
    pinnedGroupsStyle () {
      if (!this.pinnedGroupsFixed) {
        return {}
      }
      return {
        left: this.pinnedGroupsBounds.left + 'px',
        top: this.pinnedGroupsBounds.top + 'px',
        width: this.pinnedGroupsBounds.width + 'px'
      }
    },
    hasTagsToAdd () {
      const standardSelected = this.tagModal.tags.some(tag => tag.selected && !tag.existing)
      const customSelected = this.tagModal.customTags.some(tag =>
        tag.selected && !tag.existing && String(tag.label || '').trim()
      )
      return standardSelected || customSelected
    }
  },
  watch: {
    groups: {
      immediate: true,
      handler (groups) {
        groups.forEach(group => {
          this.$set(this.draftTitles, group.id, group.title)
          this.$set(this.draftReasons, group.id, group.reason || '')
        })
      }
    },
    groupsEnabled (enabled) {
      if (!enabled) {
        this.selectedEntryIDs = []
        this.closeGroupContextMenu()
      }
      this.refreshPinnedGroupsLayout()
    },
    pinnedGroups: {
      deep: true,
      handler () {
        this.refreshPinnedGroupsLayout()
      }
    }
  },
  beforeDestroy () {
    this.revokeEvidencePreviews()
    this.stopSelection()
    document.removeEventListener('paste', this.handleEvidencePaste)
    document.removeEventListener('mousedown', this.beginSelection)
    document.removeEventListener('click', this.closeGroupContextMenu)
    document.removeEventListener('keydown', this.closeGroupContextMenuOnEscape)
    document.removeEventListener('scroll', this.updatePinnedGroupsPosition, true)
    window.removeEventListener('scroll', this.updatePinnedGroupsPosition)
    window.removeEventListener('resize', this.updatePinnedGroupsPosition)
    if (this.pinnedResizeObserver) {
      this.pinnedResizeObserver.disconnect()
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.beginSelection)
    document.addEventListener('paste', this.handleEvidencePaste)
    document.addEventListener('click', this.closeGroupContextMenu)
    document.addEventListener('keydown', this.closeGroupContextMenuOnEscape)
    document.addEventListener('scroll', this.updatePinnedGroupsPosition, true)
    window.addEventListener('scroll', this.updatePinnedGroupsPosition, { passive: true })
    window.addEventListener('resize', this.updatePinnedGroupsPosition)
    this.refreshPinnedGroupsLayout()
  },
  methods: {
    refreshPinnedGroupsLayout () {
      this.$nextTick(() => {
        if (this.pinnedResizeObserver) {
          this.pinnedResizeObserver.disconnect()
          this.pinnedResizeObserver = null
        }
        if (this.$refs.pinnedGroups && window.ResizeObserver) {
          this.pinnedResizeObserver = new window.ResizeObserver(() => this.updatePinnedGroupsPosition())
          this.pinnedResizeObserver.observe(this.$refs.pinnedGroups)
        }
        this.updatePinnedGroupsPosition()
      })
    },
    updatePinnedGroupsPosition () {
      const anchor = this.$refs.pinnedAnchor
      const pinnedGroups = this.$refs.pinnedGroups
      if (!anchor || !pinnedGroups || !this.groupsEnabled || this.pinnedGroups.length === 0) {
        this.pinnedGroupsFixed = false
        this.pinnedGroupsHeight = 0
        return
      }
      const top = window.innerWidth <= 767 ? 55 : 64
      const anchorRect = anchor.getBoundingClientRect()
      this.pinnedGroupsHeight = pinnedGroups.offsetHeight
      this.pinnedGroupsBounds = {
        left: anchorRect.left,
        width: anchorRect.width,
        top
      }
      this.pinnedGroupsFixed = anchorRect.top <= top
    },
    newestGroupTimestamp (group) {
      if (group.newest_entry_date) {
        return new Date(group.newest_entry_date).getTime()
      }
      return Math.max(...group.entries.map(entry => new Date(entry.date).getTime()))
    },
    sortedGroupEntries (group) {
      return group.entries.slice().sort((left, right) => new Date(left.date) - new Date(right.date))
    },
    groupDate (group) {
      const bounds = this.groupDateBounds(group)
      if (!bounds) {
        return '-'
      }
      const oldest = this.formatDateDisplay(bounds.oldest)
      const newest = this.formatDateDisplay(bounds.newest)
      if (oldest === newest) {
        return oldest
      }
      return `${oldest} – ${newest}`
    },
    groupDateTooltip (group) {
      const bounds = this.groupDateBounds(group)
      if (!bounds) {
        return 'No date data'
      }
      return this.getDateRangeTooltip(bounds.oldest, bounds.newest)
    },
    groupDateBounds (group) {
      if (group.oldest_entry_date && group.newest_entry_date) {
        return {
          oldest: group.oldest_entry_date,
          newest: group.newest_entry_date
        }
      }
      const entries = this.sortedGroupEntries(group)
      if (entries.length === 0) {
        return null
      }
      return {
        oldest: entries[0].date,
        newest: entries[entries.length - 1].date
      }
    },
    groupServers (group) {
      const servers = new Set()
      group.entries.forEach(entry => {
        ;(entry.servers || []).forEach(server => servers.add(server))
      })
      return Array.from(servers).sort()
    },
    groupIsImportant (group) {
      return group.entries.some(entry => Boolean(entry.warning))
    },
    sortedServers (servers) {
      return Array.isArray(servers) ? servers.slice().sort() : []
    },
    getColour (server) {
      return Servers[server] ? Servers[server].colour : server
    },
    roleColour (entry) {
      const role = Roles[entry.staff_security_id]
      return role ? role.colour : '#d8dee4'
    },
    roleName (entry) {
      const role = Roles[entry.staff_security_id]
      return entry.staff_security_name || (role ? role.name : 'Player')
    },
    groupRoleColour (group) {
      const entries = group.entries.slice().sort((left, right) => new Date(right.date) - new Date(left.date))
      return entries.length > 0 ? this.roleColour(entries[0]) : '#d8dee4'
    },
    actionLabel (type) {
      if (type === 'WarnedUsers') {
        return 'Warned Users'
      }
      for (const group of this.actionLookupGroups) {
        const action = group.options.find(item => item.value === type)
        if (action) {
          return action.label
        }
      }
      return type
    },
    getIcon (type) {
      if (type === 'WarnedUsers') {
        return 'fa fa-exclamation-triangle'
      }
      for (const category in this.permissions) {
        for (const permission in this.permissions[category].Permissions) {
          if (type === permission && this.permissions[category].Permissions[permission].icon) {
            return this.permissions[category].Permissions[permission].icon
          }
        }
      }
      for (const group of this.actionLookupGroups) {
        const action = group.options.find(item => item.value === type)
        if (action) {
          return action.icon
        }
      }
      return 'fa fa-pencil'
    },
    reasonSegments (reason) {
      if (!reason) {
        return []
      }
      const parts = []
      let lastIndex = 0
      reason.replace(urlPattern, (match, url, offset) => {
        if (offset > lastIndex) {
          parts.push({ text: reason.slice(lastIndex, offset), url: false })
        }
        parts.push({ text: match, url: true })
        lastIndex = offset + match.length
        return match
      })
      if (lastIndex < reason.length) {
        parts.push({ text: reason.slice(lastIndex), url: false })
      }
      return parts
    },
    async confirmExternalLink (url) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'This link opens a page outside the tools and may be unsafe.',
        {
          title: 'Open external link?',
          okTitle: 'Open Link',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (confirmed) {
        window.open(url, '_blank', 'noopener')
      }
    },
    isExpanded (groupID) {
      return Boolean(this.expandedGroups[groupID])
    },
    toggleExpanded (groupID) {
      this.$set(this.expandedGroups, groupID, !this.expandedGroups[groupID])
    },
    isPinnedGroup (groupID) {
      return this.pinnedGroupIds.map(Number).includes(Number(groupID))
    },
    openGroupContextMenu (event, group) {
      if (!this.editableGroups || !this.groupsEnabled) {
        return
      }
      this.groupContextMenu = {
        visible: true,
        x: Math.min(event.clientX, window.innerWidth - 200),
        y: Math.min(event.clientY, window.innerHeight - 180),
        targetType: 'group',
        group,
        entry: null
      }
    },
    openEntryContextMenu (event, entry) {
      if (!this.editableGroups || !this.groupsEnabled) {
        return
      }
      event.preventDefault()
      this.groupContextMenu = {
        visible: true,
        x: Math.min(event.clientX, window.innerWidth - 200),
        y: Math.min(event.clientY, window.innerHeight - (this.groupsEnabled ? 145 : 60)),
        targetType: 'entry',
        group: null,
        entry
      }
    },
    openEvidenceUpload () {
      if (!this.groupsEnabled) {
        return
      }
      const targetType = this.groupContextMenu.targetType
      const target = targetType === 'entry' ? this.groupContextMenu.entry : this.groupContextMenu.group
      if (!target) {
        return
      }
      this.resetEvidenceUpload()
      this.evidenceUpload.targetType = targetType
      this.evidenceUpload.target = target
      this.evidenceUpload.visible = true
      this.closeGroupContextMenu()
      this.$nextTick(() => {
        if (this.$refs.evidenceInput) {
          this.$refs.evidenceInput.value = ''
        }
      })
    },
    selectEvidenceFiles (event) {
      const files = Array.from(event.target.files || [])
      this.addEvidenceFiles(files)
      event.target.value = ''
    },
    addEvidenceFiles (files) {
      const mimeExtensions = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'video/mp4': 'mp4',
        'video/x-matroska': 'mkv',
        'video/x-msvideo': 'avi',
        'video/quicktime': 'mov',
        'video/mpeg': 'mpeg'
      }
      files = files.map((file, index) => {
        if (file.name && file.name.includes('.')) {
          return file
        }
        const extension = mimeExtensions[file.type]
        if (!extension) {
          return file
        }
        return new File(
          [file],
          `clipboard-${Date.now()}-${index + 1}.${extension}`,
          { type: file.type, lastModified: file.lastModified || Date.now() }
        )
      })
      const allowed = {
        png: { mediaType: 'image', maxSize: maxEvidenceImageSize },
        jpg: { mediaType: 'image', maxSize: maxEvidenceImageSize },
        jpeg: { mediaType: 'image', maxSize: maxEvidenceImageSize },
        mp4: { mediaType: 'video', maxSize: maxEvidenceVideoSize },
        mkv: { mediaType: 'video', maxSize: maxEvidenceVideoSize },
        avi: { mediaType: 'video', maxSize: maxEvidenceVideoSize },
        mov: { mediaType: 'video', maxSize: maxEvidenceVideoSize },
        mpg: { mediaType: 'video', maxSize: maxEvidenceVideoSize },
        mpeg: { mediaType: 'video', maxSize: maxEvidenceVideoSize }
      }
      if (this.evidenceUpload.files.length + files.length > 10) {
        this.evidenceUpload.error = 'Upload at most 10 files in total.'
        return
      }
      const previews = []
      const rejectSelection = message => {
        previews.forEach(preview => {
          if (preview.url) {
            URL.revokeObjectURL(preview.url)
          }
        })
        this.evidenceUpload.error = message
      }
      for (const file of files) {
        const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : ''
        const rule = allowed[extension]
        if (!rule) {
          rejectSelection(`${file.name} has an unsupported file extension.`)
          return
        }
        if (!file.size || file.size > rule.maxSize) {
          rejectSelection(`${file.name} exceeds the ${rule.maxSize / (1024 * 1024)} MB limit.`)
          return
        }
        previews.push({
          key: `${file.name}-${file.size}-${file.lastModified}-${++this.evidenceUpload.previewSequence}`,
          file,
          mediaType: rule.mediaType,
          url: rule.mediaType === 'image' ? URL.createObjectURL(file) : ''
        })
      }
      this.evidenceUpload.error = ''
      this.evidenceUpload.files = this.evidenceUpload.files.concat(files)
      this.evidenceUpload.previews = this.evidenceUpload.previews.concat(previews)
    },
    async selectEvidenceFromClipboard () {
      if (!navigator.clipboard || typeof navigator.clipboard.read !== 'function') {
        this.evidenceUpload.error = 'Clipboard file access is not supported by this browser.'
        return
      }
      const extensions = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'video/mp4': 'mp4',
        'video/x-matroska': 'mkv',
        'video/x-msvideo': 'avi',
        'video/quicktime': 'mov',
        'video/mpeg': 'mpeg'
      }
      this.evidenceUpload.readingClipboard = true
      this.evidenceUpload.error = ''
      try {
        const clipboardItems = await navigator.clipboard.read()
        const files = []
        for (const item of clipboardItems) {
          const mimeType = item.types.find(type => extensions[type])
          if (!mimeType) {
            continue
          }
          const blob = await item.getType(mimeType)
          files.push(new File(
            [blob],
            `clipboard-${Date.now()}-${files.length + 1}.${extensions[mimeType]}`,
            { type: mimeType, lastModified: Date.now() }
          ))
        }
        if (files.length === 0) {
          this.evidenceUpload.error = 'The clipboard does not contain a supported image or video.'
          return
        }
        this.addEvidenceFiles(files)
      } catch (error) {
        this.evidenceUpload.error = 'Could not read a supported file from the clipboard.'
      } finally {
        this.evidenceUpload.readingClipboard = false
      }
    },
    handleEvidencePaste (event) {
      if (!this.evidenceUpload.visible || this.evidenceUpload.uploading) {
        return
      }
      const clipboardData = event.clipboardData
      if (!clipboardData) {
        return
      }
      const files = Array.from(clipboardData.files || [])
      if (files.length === 0 && clipboardData.items) {
        Array.from(clipboardData.items).forEach(item => {
          if (item.kind === 'file') {
            const file = item.getAsFile()
            if (file) {
              files.push(file)
            }
          }
        })
      }
      if (files.length === 0) {
        return
      }
      event.preventDefault()
      this.addEvidenceFiles(files)
    },
    removeEvidenceFile (preview) {
      if (this.evidenceUpload.uploading) {
        return
      }
      if (preview.url) {
        URL.revokeObjectURL(preview.url)
      }
      this.evidenceUpload.previews = this.evidenceUpload.previews.filter(item => item.key !== preview.key)
      this.evidenceUpload.files = this.evidenceUpload.previews.map(item => item.file)
      this.evidenceUpload.error = ''
    },
    async uploadEvidence () {
      if (!this.evidenceUpload.target || this.evidenceUpload.files.length === 0) {
        return
      }
      const files = this.evidenceUpload.files.slice()
      let uploadedCount = 0
      let latestEvidence = null
      this.evidenceUpload.uploading = true
      this.evidenceUpload.progress = 0
      this.evidenceUpload.error = ''
      try {
        for (let index = 0; index < files.length; index++) {
          const form = new FormData()
          form.append('target_type', this.evidenceUpload.targetType)
          form.append('target_id', String(this.evidenceUpload.target.id))
          form.append('files', files[index])
          const response = await axios.post('/auditlog/evidence', form, {
            onUploadProgress: progressEvent => {
              if (progressEvent.total) {
                const currentFileProgress = progressEvent.loaded / progressEvent.total
                this.evidenceUpload.progress = Math.round(((index + currentFileProgress) / files.length) * 100)
              }
            }
          })
          uploadedCount++
          if (response.data && Array.isArray(response.data.evidence)) {
            latestEvidence = response.data.evidence
          }
        }
        if (latestEvidence) {
          this.$set(this.evidenceUpload.target, 'evidence', latestEvidence)
        }
        this.evidenceUpload.visible = false
        this.$emit('evidence-changed')
        iziToast.success({
          title: 'My Activity Log',
          message: 'Evidence uploaded successfully.'
        })
      } catch (error) {
        const message = error && error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'The evidence upload failed.'
        if (latestEvidence) {
          this.$set(this.evidenceUpload.target, 'evidence', latestEvidence)
          this.$emit('evidence-changed')
        }
        if (uploadedCount > 0) {
          const uploadedFiles = new Set(files.slice(0, uploadedCount))
          this.evidenceUpload.previews.forEach(preview => {
            if (uploadedFiles.has(preview.file) && preview.url) {
              URL.revokeObjectURL(preview.url)
            }
          })
          this.evidenceUpload.files = files.slice(uploadedCount)
          this.evidenceUpload.previews = this.evidenceUpload.previews.filter(preview => !uploadedFiles.has(preview.file))
          this.evidenceUpload.error = `${uploadedCount} file${uploadedCount === 1 ? '' : 's'} uploaded. ${message}`
        } else {
          this.evidenceUpload.error = message
        }
      } finally {
        this.evidenceUpload.uploading = false
      }
    },
    resetEvidenceUpload () {
      this.revokeEvidencePreviews()
      this.evidenceUpload = {
        visible: false,
        targetType: 'entry',
        target: null,
        files: [],
        previews: [],
        uploading: false,
        readingClipboard: false,
        progress: 0,
        error: '',
        previewSequence: 0
      }
    },
    revokeEvidencePreviews () {
      const previews = this.evidenceUpload && Array.isArray(this.evidenceUpload.previews)
        ? this.evidenceUpload.previews
        : []
      previews.forEach(preview => {
        if (preview.url) {
          URL.revokeObjectURL(preview.url)
        }
      })
    },
    formatEvidenceSize (size) {
      if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(1)} MB`
      }
      return `${Math.max(1, Math.round(size / 1024))} KB`
    },
    closeGroupContextMenu () {
      if (this.groupContextMenu.visible) {
        this.groupContextMenu = {
          visible: false,
          x: 0,
          y: 0,
          targetType: 'group',
          group: null,
          entry: null
        }
      }
    },
    closeGroupContextMenuOnEscape (event) {
      if (event.key === 'Escape') {
        this.closeGroupContextMenu()
      }
    },
    toggleContextMenuGroupPin () {
      const group = this.groupContextMenu.group
      if (!group) {
        return
      }
      this.toggleGroupPin(group)
      this.closeGroupContextMenu()
    },
    openAddFieldModal () {
      const targetType = this.groupContextMenu.targetType
      const target = targetType === 'entry' ? this.groupContextMenu.entry : this.groupContextMenu.group
      if (!target) {
        return
      }
      const targetFields = Array.isArray(target.fields) ? target.fields : []
      const defaultFields = Array.isArray(this.defaultFields) ? this.defaultFields : []
      const fieldIdentity = field => `${field.type}\u0000${String(field.name || '').toLowerCase()}`
      const existing = new Set(targetFields.map(fieldIdentity))
      const defaults = new Set(defaultFields.map(fieldIdentity))
      const fields = this.fieldOptions
        .filter(option => option.value !== 'custom')
        .map(option => {
          const name = option.label
          const identity = fieldIdentity({ type: option.value, name })
          return {
            type: option.value,
            name,
            label: option.label,
            selected: existing.has(identity),
            setAsDefault: defaults.has(identity)
          }
        })
      const customFields = new Map()
      targetFields.concat(defaultFields).forEach(field => {
        if (field.type !== 'custom') {
          return
        }
        const identity = fieldIdentity(field)
        if (!customFields.has(identity)) {
          customFields.set(identity, {
            type: 'custom',
            name: field.name,
            label: field.name,
            selected: existing.has(identity),
            setAsDefault: defaults.has(identity)
          })
        }
      })
      this.fieldModal = {
        visible: true,
        targetType,
        target,
        fields: fields.concat(Array.from(customFields.values())),
        customFields: []
      }
      this.closeGroupContextMenu()
    },
    addCustomField () {
      this.fieldModal.customFields.push({
        type: 'custom',
        name: '',
        selected: true,
        setAsDefault: false
      })
    },
    selectDefaultField (field) {
      if (field.setAsDefault) {
        field.selected = true
      }
    },
    removeCustomField (index) {
      this.fieldModal.customFields.splice(index, 1)
    },
    sanitizeCustomFieldName (field) {
      field.name = field.name.replace(/[^A-Za-z0-9 ]/g, '')
    },
    applyFields () {
      const target = this.fieldModal.target
      if (!target) {
        return
      }
      const choices = this.fieldModal.fields.concat(this.fieldModal.customFields)
        .map(field => ({
          type: field.type,
          name: String(field.name || '').trim().replace(/\s+/g, ' '),
          selected: Boolean(field.selected),
          set_as_default: Boolean(field.setAsDefault)
        }))
        .filter(field => field.type !== 'custom' || field.name)
      const invalidCustom = this.fieldModal.customFields.some(field =>
        (field.selected || field.setAsDefault) && !String(field.name || '').trim()
      )
      if (invalidCustom) {
        iziToast.error({
          title: 'Field name required',
          message: 'Enter a field name using letters, numbers, and spaces.'
        })
        return
      }
      const identities = choices.map(field => `${field.type}\u0000${field.name.toLowerCase()}`)
      if (new Set(identities).size !== identities.length) {
        iziToast.error({
          title: 'Duplicate field',
          message: 'Each custom field name can only be included once.'
        })
        return
      }
      const payload = { fields: choices }
      if (this.fieldModal.targetType === 'entry') {
        payload.entry_id = Number(target.id)
      } else {
        payload.group_id = Number(target.id)
      }
      this.emit('applyAuditLogFields', payload)
      this.fieldModal.visible = false
    },
    openAddTagModal () {
      const targetType = this.groupContextMenu.targetType
      const target = targetType === 'entry' ? this.groupContextMenu.entry : this.groupContextMenu.group
      if (!target) {
        return
      }
      const existingTags = Array.isArray(target.tags) ? target.tags : []
      const existingTypes = new Set(existingTags.map(tag => tag.type))
      this.tagModal = {
        visible: true,
        targetType,
        target,
        tags: this.tagOptions
          .filter(option => option.value !== 'custom')
          .map(option => ({
            type: option.value,
            label: option.label,
            selected: existingTypes.has(option.value),
            existing: existingTypes.has(option.value)
          })),
        customTags: existingTags
          .filter(tag => tag.type === 'custom')
          .map(tag => ({
            label: tag.label,
            selected: true,
            existing: true
          }))
      }
      this.closeGroupContextMenu()
    },
    addCustomTag () {
      this.tagModal.customTags.push({
        label: '',
        selected: true,
        existing: false
      })
    },
    removeCustomTag (index) {
      this.tagModal.customTags.splice(index, 1)
    },
    sanitizeCustomTagName (tag) {
      tag.label = tag.label.replace(/[^A-Za-z0-9 ]/g, '')
    },
    applyTags () {
      const target = this.tagModal.target
      if (!target) {
        return
      }
      const customTags = this.tagModal.customTags
        .filter(tag => tag.selected && !tag.existing)
        .map(tag => ({
          type: 'custom',
          label: String(tag.label || '').trim().replace(/\s+/g, ' ')
        }))
      if (customTags.some(tag => !tag.label)) {
        iziToast.error({
          title: 'Tag name required',
          message: 'Enter a tag name using letters, numbers, and spaces.'
        })
        return
      }
      const existingLabels = new Set(
        this.tagModal.customTags
          .filter(tag => tag.existing)
          .map(tag => String(tag.label || '').trim().toLowerCase())
      )
      const customLabels = customTags.map(tag => tag.label.toLowerCase())
      if (
        new Set(customLabels).size !== customLabels.length ||
        customLabels.some(label => existingLabels.has(label))
      ) {
        iziToast.error({
          title: 'Duplicate tag',
          message: 'Each custom tag name can only be included once.'
        })
        return
      }
      const tags = this.tagModal.tags
        .filter(tag => tag.selected && !tag.existing)
        .map(tag => ({ type: tag.type, label: '' }))
        .concat(customTags)
      const currentCount = (target.tags || []).length
      if (currentCount + tags.length > 20) {
        iziToast.error({
          title: 'Tag limit reached',
          message: 'An entry or group can contain at most 20 tags.'
        })
        return
      }
      tags.forEach(tag => {
        const payload = {
          type: tag.type,
          label: tag.label
        }
        if (this.tagModal.targetType === 'entry') {
          payload.entry_id = Number(target.id)
        } else {
          payload.group_id = Number(target.id)
        }
        this.emit('createAuditLogGroupTag', payload)
      })
      this.tagModal.visible = false
    },
    toggleGroupPin (group) {
      const groupID = Number(group.id)
      const pinnedGroupIDs = this.isPinnedGroup(groupID)
        ? this.pinnedGroupIds.map(Number).filter(id => id !== groupID)
        : this.pinnedGroupIds.map(Number).concat(groupID).slice(0, 25)
      this.$emit('pinned-group-ids-changed', pinnedGroupIDs)
    },
    openDeleteGroupModal (group) {
      this.deleteModal = {
        visible: true,
        group,
        keepReasons: true
      }
    },
    deleteGroup () {
      const group = this.deleteModal.group
      if (!group) {
        return
      }
      if (this.isPinnedGroup(group.id)) {
        this.$emit(
          'pinned-group-ids-changed',
          this.pinnedGroupIds.map(Number).filter(groupID => groupID !== Number(group.id))
        )
      }
      this.emit('deleteAuditLogGroup', {
        group_id: Number(group.id),
        keep_reasons: this.deleteModal.keepReasons
      })
      this.deleteModal.visible = false
    },
    canDeleteEntry (entry) {
      return this.allowDeleteEntries || (this.allowDeleteManualEntries && Boolean(entry.manual))
    },
    async confirmDeleteEntry (entry) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Delete this audit log entry? This also deletes fields, tags, and entry evidence.',
        {
          title: 'Delete entry?',
          okTitle: 'Delete Entry',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (confirmed) {
        this.emit('deleteAuditLogEntry', {
          id: Number(entry.id),
          user_log: this.deleteEntriesFromUserLog
        })
      }
    },
    isEditingGroupReason (groupID) {
      return Boolean(this.editingGroupReasons[groupID])
    },
    startGroupReasonEdit (group) {
      if (!this.activateAuditLogEditor(`group-reason-${group.id}`, () => this.saveGroupReason(group))) {
        return
      }
      this.$set(this.draftReasons, group.id, group.reason || '')
      this.$set(this.editingGroupReasons, group.id, true)
    },
    isEditingGroupTitle (groupID) {
      return Boolean(this.editingGroupTitles[groupID])
    },
    startGroupTitleEdit (group) {
      if (!this.activateAuditLogEditor(`group-title-${group.id}`, () => this.saveGroupTitle(group))) {
        return
      }
      this.$set(this.draftTitles, group.id, group.title)
      this.$set(this.editingGroupTitles, group.id, true)
      this.$nextTick(() => {
        const input = this.$el.querySelector(`.audit-group-title-input[data-group-id="${group.id}"]`)
        if (input) {
          input.focus()
          input.select()
        }
      })
    },
    saveGroupTitle (group) {
      if (!this.isEditingGroupTitle(group.id)) {
        return
      }
      this.deactivateAuditLogEditor(`group-title-${group.id}`)
      this.$set(this.editingGroupTitles, group.id, false)
      const title = String(this.draftTitles[group.id] || '').trim()
      if (!title || title === group.title) {
        this.$set(this.draftTitles, group.id, group.title)
        return
      }
      this.$set(group, 'title', title)
      this.emit('updateAuditLogGroup', { group_id: Number(group.id), title })
    },
    cancelGroupTitleEdit (group) {
      if (!this.isEditingGroupTitle(group.id)) {
        return
      }
      this.deactivateAuditLogEditor(`group-title-${group.id}`)
      this.$set(this.editingGroupTitles, group.id, false)
      this.$set(this.draftTitles, group.id, group.title)
    },
    saveGroupReason (group) {
      if (!this.isEditingGroupReason(group.id)) {
        return
      }
      this.deactivateAuditLogEditor(`group-reason-${group.id}`)
      this.$set(this.editingGroupReasons, group.id, false)
      const reason = String(this.draftReasons[group.id] || '').trim()
      if (reason === (group.reason || '')) {
        return
      }
      this.$set(group, 'reason', reason)
      this.emit('updateAuditLogGroup', { group_id: Number(group.id), reason })
    },
    cancelGroupReasonEdit (group) {
      if (!this.isEditingGroupReason(group.id)) {
        return
      }
      this.deactivateAuditLogEditor(`group-reason-${group.id}`)
      this.$set(this.editingGroupReasons, group.id, false)
      this.$set(this.draftReasons, group.id, group.reason || '')
    },
    isEditingEntryReason (entryID) {
      return Boolean(this.editingEntryReasons[entryID])
    },
    startEntryReasonEdit (entry) {
      if (!this.activateAuditLogEditor(`entry-reason-${entry.id}`, () => this.saveEntryReason(entry))) {
        return
      }
      this.$set(this.draftEntryReasons, entry.id, entry.reason || '')
      this.$set(this.editingEntryReasons, entry.id, true)
    },
    saveEntryReason (entry) {
      if (!this.isEditingEntryReason(entry.id)) {
        return
      }
      this.deactivateAuditLogEditor(`entry-reason-${entry.id}`)
      this.$set(this.editingEntryReasons, entry.id, false)
      const reason = String(this.draftEntryReasons[entry.id] || '').trim()
      if (!reason || reason === (entry.reason || '')) {
        return
      }
      this.$set(entry, 'reason', reason)
      this.emit('updateLogReason', { id: Number(entry.id), reason })
    },
    cancelEntryReasonEdit (entry) {
      if (!this.isEditingEntryReason(entry.id)) {
        return
      }
      this.deactivateAuditLogEditor(`entry-reason-${entry.id}`)
      this.$set(this.editingEntryReasons, entry.id, false)
      this.$set(this.draftEntryReasons, entry.id, entry.reason || '')
    },
    removeFromGroup (group, entry) {
      const totalMemberCount = Number(group.total_member_count || (group.entries || []).length)
      const entries = (group.entries || []).filter(item => Number(item.id) !== Number(entry.id))
      this.$set(group, 'entries', entries)
      this.$set(group, 'total_member_count', Math.max(0, totalMemberCount - 1))
      this.emit('removeAuditLogGroupEntry', {
        group_id: Number(group.id),
        entry_id: Number(entry.id)
      })
    },
    isSelected (entryID) {
      return this.selectedEntryIDs.includes(Number(entryID))
    },
    setEntrySelected (entryID, selected) {
      entryID = Number(entryID)
      if (selected && !this.isSelected(entryID)) {
        this.selectedEntryIDs = this.selectedEntryIDs.concat(entryID)
      } else if (!selected && this.isSelected(entryID)) {
        this.selectedEntryIDs = this.selectedEntryIDs.filter(id => id !== entryID)
      }
    },
    startDrag (event, entryID) {
      entryID = Number(entryID)
      this.draggedEntryID = entryID
      if (!this.isSelected(entryID)) {
        this.selectedEntryIDs = [entryID]
      }
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(entryID))
    },
    finishDrag () {
      this.draggedEntryID = null
      this.dropTarget = null
    },
    draggedIDs () {
      if (this.draggedEntryID && this.isSelected(this.draggedEntryID)) {
        return this.selectedEntryIDs.slice()
      }
      return this.draggedEntryID ? [this.draggedEntryID] : []
    },
    canDropOnEntry (targetEntry) {
      const entryIDs = new Set(this.draggedIDs())
      entryIDs.add(Number(targetEntry.id))
      return this.draggedIDs().length > 0 && entryIDs.size >= 2
    },
    canDropOnGroup (group) {
      if (this.draggedIDs().length === 0) {
        return false
      }
      const existingIDs = new Set((group.entries || []).map(entry => Number(entry.id)))
      return this.draggedIDs().some(entryID => !existingIDs.has(entryID))
    },
    dragOverEntry (event, targetEntry) {
      if (!this.canDropOnEntry(targetEntry)) {
        return
      }
      event.dataTransfer.dropEffect = 'move'
      const id = Number(targetEntry.id)
      if (!this.isEntryDropTarget(id)) {
        this.dropTarget = { kind: 'entry', id }
      }
    },
    dragOverGroup (event, group) {
      if (!this.canDropOnGroup(group)) {
        return
      }
      event.dataTransfer.dropEffect = 'move'
      const id = Number(group.id)
      if (!this.isGroupDropTarget(id)) {
        this.dropTarget = { kind: 'group', id }
      }
    },
    leaveDropTarget (event) {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return
      }
      this.dropTarget = null
    },
    isEntryDropTarget (entryID) {
      return this.dropTarget && this.dropTarget.kind === 'entry' && this.dropTarget.id === Number(entryID)
    },
    isGroupDropTarget (groupID) {
      return this.dropTarget && this.dropTarget.kind === 'group' && this.dropTarget.id === Number(groupID)
    },
    async dropOnEntry (targetEntry) {
      if (!this.editableGroups || !this.groupsEnabled) {
        return
      }
      this.dropTarget = null
      const entryIDs = this.draggedIDs()
      const targetID = Number(targetEntry.id)
      if (!entryIDs.includes(targetID)) {
        entryIDs.push(targetID)
      }
      if (entryIDs.length < 2) {
        return
      }
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Create a new group from these ${entryIDs.length} entries?`,
        {
          title: 'Create group?',
          okTitle: 'Create Group',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (!confirmed) {
        this.draggedEntryID = null
        return
      }
      const selectedEntries = this.entries.filter(entry => entryIDs.includes(Number(entry.id)))
      const reasons = Array.from(new Set(selectedEntries.map(entry => String(entry.reason || '').trim()).filter(Boolean)))
      const includeFields = await this.confirmIncludeEntryFields(entryIDs)
      this.emit('createAuditLogGroup', {
        entry_ids: entryIDs,
        title: 'New Group',
        reason: reasons.length === 1 ? reasons[0] : '',
        include_fields: includeFields
      })
      this.selectedEntryIDs = []
      this.draggedEntryID = null
    },
    async dropOnGroup (group) {
      if (!this.editableGroups || !this.groupsEnabled) {
        return
      }
      this.dropTarget = null
      const existingIDs = new Set(group.entries.map(entry => Number(entry.id)))
      const entryIDs = this.draggedIDs().filter(entryID => !existingIDs.has(entryID))
      if (entryIDs.length === 0) {
        return
      }
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Add ${entryIDs.length} ${entryIDs.length === 1 ? 'entry' : 'entries'} to "${group.title}"?`,
        {
          title: 'Add to group?',
          okTitle: 'Add to Group',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (!confirmed) {
        this.draggedEntryID = null
        return
      }
      const includeFields = await this.confirmIncludeEntryFields(entryIDs)
      const movedEntries = this.entries.filter(entry => entryIDs.includes(Number(entry.id)))
      const currentEntries = Array.isArray(group.entries) ? group.entries : []
      this.$set(group, 'entries', currentEntries.concat(movedEntries))
      this.$set(group, 'total_member_count', Number(group.total_member_count || currentEntries.length) + movedEntries.length)
      this.emit('addAuditLogGroupEntries', {
        group_id: Number(group.id),
        entry_ids: entryIDs,
        include_fields: includeFields
      })
      this.selectedEntryIDs = []
      this.draggedEntryID = null
    },
    entriesHaveTransferableFields (entryIDs) {
      const selectedIDs = new Set(entryIDs.map(Number))
      return this.entries.some(entry => {
        if (!selectedIDs.has(Number(entry.id))) {
          return false
        }
        return (entry.fields || []).some(field => {
          if (field.type === 'usernames') {
            return Array.isArray(field.users) && field.users.length > 0
          }
          return Boolean(String(field.value || '').trim())
        })
      })
    },
    async confirmIncludeEntryFields (entryIDs) {
      if (!this.entriesHaveTransferableFields(entryIDs)) {
        return false
      }
      return this.$bvModal.msgBoxConfirm(
        'These entries contain fields. Add their values to the group as well?',
        {
          title: 'Include fields?',
          okTitle: 'Include Fields',
          cancelTitle: 'Only Entries',
          centered: true,
          hideHeaderClose: true,
          noCloseOnBackdrop: true,
          noCloseOnEsc: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
    },
    beginSelection (event) {
      if (!this.editableGroups || !this.groupsEnabled || event.button !== 0) {
        return
      }
      const target = event.target
      if (target && target.closest && target.closest('button, input, textarea, select, a, label, [contenteditable="true"]')) {
        return
      }
      const surface = this.$refs.selectionSurface
      if (!surface || !surface.contains(target)) {
        return
      }
      const rect = surface.getBoundingClientRect()
      this.selectionStart = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      this.selectionBaseIDs = this.selectedEntryIDs.slice()
      this.selectionBox = {
        left: this.selectionStart.x,
        top: this.selectionStart.y,
        width: 0,
        height: 0
      }
      window.addEventListener('mousemove', this.updateSelection)
      window.addEventListener('mouseup', this.stopSelection)
      event.preventDefault()
    },
    updateSelection (event) {
      if (!this.selectionStart || !this.$refs.selectionSurface) {
        return
      }
      this.selectionPointer = { x: event.clientX, y: event.clientY }
      if (this.selectionFrame !== null) {
        return
      }
      this.selectionFrame = window.requestAnimationFrame(this.applySelection)
    },
    applySelection () {
      this.selectionFrame = null
      if (!this.selectionStart || !this.$refs.selectionSurface || !this.selectionPointer) {
        return
      }
      const surfaceRect = this.$refs.selectionSurface.getBoundingClientRect()
      const currentX = this.selectionPointer.x - surfaceRect.left
      const currentY = this.selectionPointer.y - surfaceRect.top
      const left = Math.min(this.selectionStart.x, currentX)
      const top = Math.min(this.selectionStart.y, currentY)
      const right = Math.max(this.selectionStart.x, currentX)
      const bottom = Math.max(this.selectionStart.y, currentY)
      this.selectionBox = {
        left,
        top,
        width: right - left,
        height: bottom - top
      }

      const baseSelection = new Set(this.selectionBaseIDs)
      const selected = new Set(this.selectionBaseIDs)
      this.$refs.selectionSurface.querySelectorAll('[data-entry-id]').forEach(element => {
        const rect = element.getBoundingClientRect()
        const relative = {
          left: rect.left - surfaceRect.left,
          right: rect.right - surfaceRect.left,
          top: rect.top - surfaceRect.top,
          bottom: rect.bottom - surfaceRect.top
        }
        if (relative.left <= right && relative.right >= left && relative.top <= bottom && relative.bottom >= top) {
          const entryID = Number(element.getAttribute('data-entry-id'))
          if (baseSelection.has(entryID)) {
            selected.delete(entryID)
          } else {
            selected.add(entryID)
          }
        }
      })
      const nextSelection = Array.from(selected).sort((left, right) => left - right)
      const currentSelection = this.selectedEntryIDs.slice().sort((left, right) => left - right)
      if (nextSelection.length !== currentSelection.length || nextSelection.some((id, index) => id !== currentSelection[index])) {
        this.selectedEntryIDs = nextSelection
      }
    },
    stopSelection () {
      window.removeEventListener('mousemove', this.updateSelection)
      window.removeEventListener('mouseup', this.stopSelection)
      if (this.selectionFrame !== null) {
        window.cancelAnimationFrame(this.selectionFrame)
        this.selectionFrame = null
      }
      this.selectionStart = null
      this.selectionBox = null
      this.selectionBaseIDs = []
      this.selectionPointer = null
    },
    activateAuditLogEditor (key, save) {
      if (this.activeEditor && this.activeEditor.key !== key) {
        const previous = this.activeEditor
        this.activeEditor = null
        if (previous.save() === false) {
          this.activeEditor = previous
          return false
        }
      }
      this.activeEditor = { key, save }
      return true
    },
    deactivateAuditLogEditor (key) {
      if (this.activeEditor && this.activeEditor.key === key) {
        this.activeEditor = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-log-selection-surface {
  position: relative;
  min-height: 120px;
}

.audit-selection-box {
  position: absolute;
  z-index: 20;
  pointer-events: none;
  border: 1px solid rgba(32, 168, 216, 0.9);
  background: rgba(32, 168, 216, 0.15);
}

.audit-selection-summary {
  position: fixed;
  z-index: 1040;
  right: 20px;
  bottom: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 34px;
  padding: 6px 10px;
  border: 1px solid rgba(32, 168, 216, 0.4);
  background: rgba(27, 39, 46, 0.96);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.38);
  color: #d9f5ff;
}

.audit-selection-summary .btn {
  padding: 0 2px;
  margin-left: auto;
  color: #63c2de;
}

.audit-log-list {
  gap: 10px;
}

.audit-pinned-anchor {
  position: relative;
  margin-bottom: 10px;
}

.audit-pinned-groups {
  z-index: 18;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border: 1px solid rgba(248, 184, 78, 0.35);
  background: rgba(30, 34, 38, 0.98);
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.28);
}

.audit-pinned-groups-fixed {
  position: fixed;
  z-index: 1020;
  max-height: calc(100vh - 76px);
  overflow-y: auto;
}

.audit-pinned-groups > * {
  flex: 0 0 auto;
  width: 100%;
}

.audit-group-context-menu {
  position: fixed;
  z-index: 1100;
  min-width: 170px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: #252b31;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.42);
}

.audit-group-context-menu button {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 9px;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  color: #d8dee4;
  text-align: left;
}

.audit-group-context-menu button:hover {
  background: rgba(255, 255, 255, 0.08);
}

.audit-modal-label {
  display: block;
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.audit-field-picker-header,
.audit-field-picker-row {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 120px 36px;
  gap: 10px;
  align-items: center;
}

.audit-field-picker-header {
  padding: 0 8px 7px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.audit-field-picker-row {
  min-height: 42px;
  padding: 6px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-tag-picker-header,
.audit-tag-picker-row {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 110px;
  gap: 10px;
  align-items: center;
}

.audit-tag-picker-header {
  padding: 0 8px 7px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.audit-tag-picker-row {
  min-height: 42px;
  padding: 6px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-custom-tag-row {
  grid-template-columns: 48px minmax(0, 1fr) 110px;
}

.audit-custom-tag-row .btn {
  justify-self: end;
}

.audit-upload-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.audit-upload-preview {
  position: relative;
  display: flex;
  min-width: 0;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  flex-direction: column;
  gap: 5px;
  background: rgba(0, 0, 0, 0.16);
}

.audit-upload-remove {
  position: absolute;
  z-index: 2;
  top: 5px;
  right: 5px;
  opacity: 0.78;
}

.audit-upload-preview img,
.audit-upload-video-icon {
  display: flex;
  width: 100%;
  height: 110px;
  object-fit: contain;
  align-items: center;
  justify-content: center;
  background: #11161a;
}

.audit-upload-video-icon {
  color: #63c2de;
  font-size: 2.5rem;
}

.audit-upload-preview > span:not(.audit-upload-video-icon) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audit-upload-preview small {
  color: rgba(255, 255, 255, 0.58);
}

.audit-log-entry {
  position: relative;
  background: rgba(255, 255, 255, 0.025);
  border-color: rgba(255, 255, 255, 0.08);
  transition: border-color 0.06s ease, background-color 0.06s ease;
}

.audit-entry-selected {
  border-color: #20a8d8;
  background: rgba(32, 168, 216, 0.1);
}

.audit-card-checkbox,
.audit-card-checkbox input {
  cursor: pointer;
}

.audit-drop-target {
  border-color: #4dbd74 !important;
  background: rgba(77, 189, 116, 0.16) !important;
  box-shadow: inset 0 0 0 1px rgba(77, 189, 116, 0.45);
}

.audit-log-date {
  position: absolute;
  top: 12px;
  right: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  max-width: 45%;
  color: rgba(255, 255, 255, 0.62);
  text-align: right;
}

.audit-expand-button,
.audit-remove-member {
  border: 0;
  background: transparent;
}

.audit-expand-button {
  position: absolute;
  z-index: 2;
  top: 50%;
  right: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  color: #d8dee4;
  transform: translateY(-50%);
}

.audit-expand-button:focus,
.audit-expand-button:active {
  outline: 0 !important;
  box-shadow: none !important;
}

.audit-log-main-row {
  align-items: center;
}

.audit-group-summary {
  position: relative;
  min-height: 66px;
}

.audit-log-icon {
  display: flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.audit-group-icon {
  color: #f8b84e;
  font-size: 1.25rem;
}

.audit-log-content {
  min-width: 0;
  padding-right: 210px;
}

.audit-log-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  margin-bottom: 4px;
}

.audit-staff-name,
.audit-group-title {
  font-weight: 700;
}

.audit-log-message,
.auditReason,
.audit-group-message {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.audit-log-message,
.audit-action-label {
  display: block;
}

.audit-action-label {
  margin-top: 2px;
}

.auditReason {
  display: inline-block;
  margin: 0;
  padding: 2px 5px;
}

.auditReason a {
  color: #59dcc2;
  text-decoration: underline;
}

.audit-group-title-row,
.audit-reason-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 7px;
}

.audit-group-title-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.audit-group-title-button:hover {
  color: #63c2de;
  text-decoration: underline;
}

.audit-reason-inline {
  flex: 0 1 auto;
  align-items: center;
  min-width: 0;
  margin-top: 0;
}

.audit-reason-inline .audit-reason-editor {
  width: min(560px, 100%);
}

.audit-reason-inline .audit-reason-edit {
  white-space: nowrap;
}

.audit-reason-missing {
  flex: 0 0 auto;
}

.audit-group-title-input {
  max-width: 520px;
  height: 34px;
  font-weight: 700;
}

.audit-group-title-row {
  gap: 3px;
}

.audit-group-actions {
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.audit-delete-group,
.audit-title-edit,
.audit-reason-edit {
  min-height: 30px;
  padding: 3px 6px;
}

.audit-delete-group,
.audit-title-edit {
  padding-right: 4px;
  padding-left: 4px;
}

.audit-reason-editor {
  min-width: 280px;
  min-height: 58px;
}

.audit-group-members {
  margin: 14px 0 0 110px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-group-members-selectable {
  margin-left: 138px;
}

.audit-group-member {
  display: grid;
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-height: 42px;
  padding: 10px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.audit-group-member-editable {
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto 30px;
}

.audit-group-member-deletable {
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto 30px;
}

.audit-group-member-editable.audit-group-member-deletable {
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto 30px 30px;
}

.audit-group-number {
  color: #f39c3d;
  font-weight: 700;
  text-align: right;
}

.audit-group-entry-icon {
  color: rgba(255, 255, 255, 0.78);
  text-align: center;
}

.audit-manual-entry-icon {
  color: #20a8d8 !important;
}

.audit-delete-entry {
  border: 0;
  background: transparent;
  color: #f86c6b;
}

.audit-delete-entry-card {
  position: absolute;
  right: 12px;
  bottom: 10px;
}

.audit-group-member-content {
  min-width: 0;
}

.audit-group-member-servers {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: 8px;
}

.audit-group-member-date {
  color: rgba(255, 255, 255, 0.58);
  white-space: nowrap;
}

.audit-entry-drag-handle {
  cursor: grab;
}

.audit-entry-drag-handle:active {
  cursor: grabbing;
}

.audit-entry-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  margin: 0;
}

.audit-entry-checkbox input {
  margin: 0;
}

.audit-card-checkbox {
  flex: 0 0 24px;
  margin-right: 4px;
}

.audit-checkbox-placeholder {
  visibility: hidden;
}

.audit-remove-member {
  color: #f86c6b;
}

.audit-empty-state {
  padding: 36px 16px;
  color: rgba(255, 255, 255, 0.58);
  text-align: center;
}

@media (max-width: 767px) {
  .audit-selection-summary {
    right: 12px;
    bottom: 12px;
  }

  .audit-pinned-groups-fixed {
    max-height: calc(100vh - 67px);
  }

  .audit-log-meta {
    flex-wrap: wrap;
  }

  .audit-log-date {
    position: static;
    max-width: none;
    margin-bottom: 8px;
  }

  .audit-log-content {
    padding-right: 0;
  }

  .audit-group-members {
    margin-left: 0;
  }

  .audit-group-member {
    grid-template-columns: 32px 22px minmax(0, 1fr);
  }

  .audit-group-member-editable {
    grid-template-columns: 32px 22px minmax(0, 1fr) 28px;
  }

  .audit-group-member-deletable {
    grid-template-columns: 32px 22px minmax(0, 1fr) 28px;
  }

  .audit-group-member-editable.audit-group-member-deletable {
    grid-template-columns: 32px 22px minmax(0, 1fr) 28px 28px;
  }

  .audit-group-member-date {
    display: none;
  }

  .audit-group-member-servers {
    display: flex;
    margin: 5px 0 0;
  }
}
</style>
